/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';

import { buildHashWithAuthMode, getLocationHash, serializeQueryOmittingAuthMode } from 'js/lib/authModeDeepLinkUtils';
import { useLocation, useNavigate, useParams } from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Button, Grid, InlineNotification } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import {
  useEnterpriseLearningPathCollectionId,
  useEnterpriseLearningPathPageData,
} from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathContext';
import { getEnrollingHeroActionId } from 'bundles/enterprise-learning-path-description-page/contexts/SkillPathwayActivelyEnrollingContext';
import {
  isDigitalBadgeAgreementAccepted,
  setDigitalBadgeAgreementAccepted,
} from 'bundles/enterprise-learning-path-description-page/utils/isDigitalBadgeAgreementAccepted';
import EnterpriseBadgeEnrollConfirmationModal from 'bundles/enterprise-legacy-learner-home/components/modals/EnterpriseBadgeEnrollConfirmationModal';
import useEnterpriseBadgeEnrollMutation from 'bundles/enterprise-legacy-learner-home/queryHooks/useEnterpriseBadgeEnrollMutation';
import { useNaptime } from 'bundles/naptimejs';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import ProgramEnrollmentsV2 from 'bundles/naptimejs/resources/programEnrollments.v2';
import { useFetchIsCourseraBadge } from 'bundles/s12n-common/api/useFetchIsCourseraBadge';
import useGetSkillPathwayCourseStartLink from 'bundles/skills-common/shared/verified-skills/hooks/useGetSkillPathwayCourseStartLink';
import { useEnterpriseModalManager } from 'bundles/unified-common/contexts/EnterpriseModalManagerContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgram, useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';
import EnterpriseDisplayEnrollButton from 'bundles/unified-description-page-common/components/enroll/enterprise/EnterpriseDisplayEnrollButton';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

const styles = {
  confirmationButton: css`
    margin-right: var(--cds-spacing-300);
  `,
};

const EnterpriseLearningPathEnrollButtonNotConfigurable = ({
  enrollStateText,
  redirectToCourseHomeOnEnrollSuccess,
  enrollButtonStylesOverride,
  setCurrentEnrollingAction,
  disabled,
  additionalTrackingEvent,
  buttonDataTestId,
  isSkillPathway,
}: {
  enrollStateText?: string;
  redirectToCourseHomeOnEnrollSuccess?: boolean;
  enrollButtonStylesOverride?: SerializedStyles;
  setCurrentEnrollingAction?: (action: string | undefined) => void;
  additionalTrackingEvent?: () => void;
  disabled?: boolean;
  buttonDataTestId?: string;
  isSkillPathway?: boolean;
}) => {
  const track = useTracker();
  const program = useProgram();
  const navigate = useNavigate();
  const location = useLocation();
  const { programSlug, orgSlug } = useParams();
  const isOrgScopedHome = useIsOrgScopedHome();
  const fetchBadgeSource = useFetchIsCourseraBadge();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const productFeatures = useEnterpriseProductFeatures();
  const isMember = useProgramMembership()?.isProgramMember;
  const pageData = useEnterpriseLearningPathPageData();
  const collectionId = useEnterpriseLearningPathCollectionId();
  const enroll = useEnterpriseBadgeEnrollMutation();
  const modalManager = useEnterpriseModalManager();
  const { fetchSkillPathwayCourseStartLink } = useGetSkillPathwayCourseStartLink(
    pageData?.enterpriseBadge?.badgeActivities[0]?.id || '',
    pageData?.enterpriseBadge?.badgeActivities[0]?.slug || ''
  );
  const [isEnrolling, setIsEnrolling] = React.useState(false);
  const [enrollError, setEnrollError] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { naptime } = useNaptime(() => ({}));

  React.useEffect(() => {
    if (isEnrolling) {
      setCurrentEnrollingAction?.(getEnrollingHeroActionId());
    } else {
      setCurrentEnrollingAction?.(undefined);
    }
  }, [setCurrentEnrollingAction, isEnrolling]);

  if (!pageData) {
    return null;
  }
  const id = pageData?.enterpriseBadge?.id;

  const onActionSuccess = async () => {
    track('click_button', {
      button: { name: 'enroll' },
      enterprise: {
        ...(thirdPartyOrganization?.slug ? { organizationSlug: thirdPartyOrganization?.slug } : {}),
        ...(programSlug ? { programSlug } : {}),
      },
      product: {
        id,
        type: 'learning_path',
        ...(isSkillPathway ? { verifiedSkillPath: true } : {}),
      },
    });

    if (isSkillPathway) {
      const skillPathwayCourseStartLink = await fetchSkillPathwayCourseStartLink();
      window.open(skillPathwayCourseStartLink, '_self');
    } else if (redirectToCourseHomeOnEnrollSuccess) {
      window.open(`/learn/${pageData?.enterpriseBadge?.badgeActivities[0]?.slug}/home/welcome`, '_self');
    } else {
      window.open(`/learn/${pageData?.enterpriseBadge?.badgeActivities[0]?.slug}`, '_self');
    }
  };

  const handleEnroll = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!isMember) {
      modalManager?.setIsAccessModalDismissed(false);
      setIsModalOpen(false);
      return;
    }

    if (isEnrolling) return;
    try {
      setEnrollError(false);
      setIsEnrolling(true);
      await enroll({
        user_id: user.get().id,
        badge_template_id: id,
        request_origin: 'REQUEST_ORIGIN_USER',
      });
      await naptime.executeMutation(
        ProgramEnrollmentsV2.action(
          'enrollInCourse',
          {},
          {
            programId: program?.id ?? '',
            courseId: pageData?.enterpriseBadge?.badgeActivities[0]?.id ?? '',
            userId: user.get().id.toString(),
            collectionId,
          }
        )
      );
      onActionSuccess();
    } catch (error) {
      setIsEnrolling(false);

      const errorCode = error?.responseJSON?.errorCode;
      // If they are already enrolled, continue with sending them to the first course
      if (errorCode === 'ALREADY_ENROLLED_ERROR') {
        onActionSuccess();
      } else if (errorCode === 'ENROLL_IN_COURSE_ERROR') {
        // The message says "The course must be in available state"
        // This means the course is completed
        window.open(
          isOrgScopedHome
            ? `/organizations/${orgSlug}/my-learning?myLearningTab=IN_PROGRESS`
            : `/programs/${programSlug}/my-learning?myLearningTab=IN_PROGRESS`,
          '_self'
        );
      } else {
        setEnrollError(true);
        setIsModalOpen(false);
      }
    }
  };

  if (pageData?.enterpriseBadge?.badgeActivities.length < 1 || productFeatures?.isBrowseOnlyProgram) {
    return null;
  } else if (!user.isAuthenticatedUser()) {
    return (
      <div css={{ width: '250px' }}>
        <EnterpriseDisplayEnrollButton
          onClick={() => {
            additionalTrackingEvent?.();
            const searchSerialized = serializeQueryOmittingAuthMode(location.query);
            navigate({
              pathname: location.pathname,
              search: searchSerialized ? `?${searchSerialized}` : '',
              hash: buildHashWithAuthMode(getLocationHash(location), 'login'),
            });
          }}
          trackingName="logged_out_enroll_button"
          dataE2eName="login-enroll-button"
          customMessage={enrollStateText || _t('Enroll in learning path')}
          stylesOverride={enrollButtonStylesOverride}
        />
      </div>
    );
  } else if (!program || !thirdPartyOrganization) {
    return null;
  }

  return (
    <>
      <Grid container direction="column" spacing={12}>
        {enrollError && (
          <Grid item>
            <InlineNotification severity="error">{_t('Error enrolling, please try again')}</InlineNotification>
          </Grid>
        )}
        <Grid item>
          <Button
            disabled={disabled}
            loading={isEnrolling}
            css={enrollButtonStylesOverride}
            onClick={async (event) => {
              additionalTrackingEvent?.();

              const isCourseraBadge = await fetchBadgeSource(pageData.enterpriseBadge.id);
              if (isDigitalBadgeAgreementAccepted() || isCourseraBadge) {
                handleEnroll(event);
              } else {
                setIsModalOpen(true);
              }
            }}
            data-testid={buttonDataTestId}
          >
            {enrollStateText || _t('Enroll in learning path')}
          </Button>
        </Grid>
      </Grid>
      <EnterpriseBadgeEnrollConfirmationModal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Button
          css={styles.confirmationButton}
          loading={isEnrolling}
          onClick={(event) => {
            additionalTrackingEvent?.();
            handleEnroll(event);
            setDigitalBadgeAgreementAccepted();
          }}
        >
          {_t('Enroll and go to course')}
        </Button>
        <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
          {_t('Cancel')}
        </Button>
      </EnterpriseBadgeEnrollConfirmationModal>
    </>
  );
};

export default EnterpriseLearningPathEnrollButtonNotConfigurable;
